import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";


const BrandingContext = createContext({ branding: null, refreshBranding: () => {} });

function hexToHsl(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return null;
  let r = parseInt(m[1], 16) / 255, g = parseInt(m[2], 16) / 255, b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null);

  const refreshBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branding`);
      setBranding(res.data);
    } catch {
      setBranding(null);
    }
  }, []);

  useEffect(() => { refreshBranding(); }, [refreshBranding]);

  // Apply the brand accent to the whole app via the design token.
  useEffect(() => {
    const root = document.documentElement;
    const hsl = branding?.accent_color ? hexToHsl(branding.accent_color) : null;
    if (hsl) {
      root.style.setProperty("--accent", hsl);
    } else {
      root.style.removeProperty("--accent");
    }
    if (branding?.app_name) {
      document.title = branding.app_name;
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
